import { z } from 'zod';

/* ============================== THE MENU (plan §22.7) ==============================
 *
 * The header and footer navigation, as content. Until now the nav was `NAV` and `FOOTER_*` in
 * `apps/login/src/site/content/data.ts` — arrays of message descriptors compiled into the bundle — so
 * adding a link meant a developer and a deploy. That was tolerable while the page set was fixed in code
 * too; it stopped being tolerable the moment an admin could add a page and then not link to it.
 *
 * ⚠ IT RIDES THE `chrome` DOCUMENT, WHICH ALREADY EXISTED FOR EXACTLY THIS. `chrome` has been a reserved
 * slug since the first slice: a page row with no route, carried through publish, restore and the payload
 * like any other. So the menu needs NO new storage, NO schema change to releases, and NO new publish
 * path — it is a section on a document that was already travelling, and `routeOf('chrome')` returning
 * null is what stops it being written out as a junk page.
 */

/**
 * Where a menu item points.
 *
 * ⚠ `anchor` IS NOT A LUXURY — the shipped nav already has one (`/competitions#programmes`). Modelling
 * only pages and external URLs would have silently dropped it on the first publish.
 */
export const menuTargetSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('page'), slug: z.string().min(1) }),
  z.object({ kind: z.literal('anchor'), slug: z.string().min(1), hash: z.string().min(1) }),
  z.object({ kind: z.literal('external'), url: z.string().min(1) })
]);

export type SiteMenuTarget = z.infer<typeof menuTargetSchema>;

export const menuItemSchema = z.object({
  /** Stable per item, so reordering does not orphan an edit. */
  id: z.string().min(1),
  /** What the visitor reads. Content, and therefore per locale. */
  label: z.string(),
  /** Hidden keeps the item and its target — hiding is not deleting, as everywhere else here. */
  visible: z.boolean(),
  /**
   * Where the item goes, or nothing at all.
   *
   * ⚠ OPTIONAL ONLY SO A PARENT CAN BE A PURE LABEL. "Programmes" opening a submenu is a real nav
   * pattern and it points nowhere itself. This deliberately WEAKENS the schema — a target-less item is
   * now structurally valid — and the guarantee it used to carry moves to `menuTree`/`resolveMenu`,
   * which drop an item that has neither a destination nor children.
   *
   * ⚠ THE GUARANTEE HAD TO MOVE RATHER THAN BE ADDED HERE, because `parseSections` is TOLERANT: a
   * cross-item rule ("target-less is fine only with children") could only live on the whole menu, and
   * failing it would drop the entire `menu` section — blanking the site's navigation over one bad row.
   * A resolver that drops one item degrades to a shorter menu instead.
   */
  target: menuTargetSchema.optional(),
  /**
   * The header item this one hangs under, by `id`. Absent = top level.
   *
   * ⚠ HEADER ONLY, AND FLAT-WITH-A-POINTER RATHER THAN NESTED. `group` already proved the shape: a flat
   * array with an attribute keeps reorder, add and remove as ordinary array operations, whereas nested
   * `children` would need a recursive Zod schema wrapping a discriminated union and would turn every
   * editor action into a tree walk. The footer's columns stay `group`; a footer with dropdowns is not a
   * footer.
   *
   * ⚠ AND THE TREE IS EXACTLY ONE LEVEL DEEP — see `menuTree`, which CLAMPS rather than rejects.
   */
  parentId: z.string().optional(),
  /**
   * The footer column this item sits under. Ignored by the header.
   *
   * ⚠ THE FOOTER HAS TWO HEADED COLUMNS AND FLATTENING IT WOULD HAVE BEEN A SILENT DESIGN REGRESSION.
   * D22.3 chose a "flat" menu, but that was about NESTING — no dropdowns — not about collapsing the
   * footer into a single list. One optional string reproduces the existing layout exactly.
   */
  group: z.string().optional()
});

export type SiteMenuItem = z.infer<typeof menuItemSchema>;

export const menuFieldsSchema = z.object({
  header: z.array(menuItemSchema),
  footer: z.array(menuItemSchema)
});

export type SiteMenuFields = z.infer<typeof menuFieldsSchema>;

/**
 * An external URL that is safe to put in an `href`, or null.
 *
 * ⚠ THIS IS THE ONE PLACE IN THE MENU THAT IS A SECURITY BOUNDARY. A menu item's target is authored in a
 * console and rendered into an anchor on EVERY page of the site — the most-visited element there is. A
 * `javascript:` URL there is stored XSS with the widest possible blast radius, and `data:` is the same
 * hole wearing a different scheme.
 *
 * ⚠ AND IT IS CHECKED AT RENDER, NOT ONLY AT SAVE. The write validates too, but a document can reach a
 * browser from a release published before this rule existed, or from a server that has not been
 * deployed yet. A link that cannot be made safe is dropped rather than rendered inert: an anchor with no
 * href is a menu item that looks clickable and is not.
 */
export function safeExternalUrl(raw: string): string | null {
  const value = raw.trim();

  // Parsed rather than pattern-matched: `java\tscript:` and `JavaScript:` both defeat a naive prefix
  // test, and the URL parser is the thing browsers actually agree with.
  let parsed: URL;
  try {
    parsed = new URL(value);
  } catch {
    return null;
  }

  return parsed.protocol === 'http:' || parsed.protocol === 'https:' ? parsed.toString() : null;
}

/** What a menu item resolves to, once its target has been checked against what actually exists. */
export interface ResolvedMenuItem {
  id: string;
  label: string;
  /** Ready for an `href` / `to`. */
  href: string;
  /** True for an external destination — the caller adds `rel` and any outward marker. */
  external: boolean;
  group?: string;
}

/**
 * How many levels of menu the site will draw, counting the top one.
 *
 * ⚠ THREE IS A PRODUCT DECISION, NOT A TECHNICAL LIMIT. The tree below is fully recursive and would
 * happily build ten; what stops at three is what a reader can follow in a dropdown and what the editor's
 * parent picker can present without becoming a puzzle. Raising it is a one-character change here — the
 * model, the renderers and the picker all read this constant — but it should be a decision about the
 * navigation, not a side effect of somebody needing one more level once.
 */
export const MENU_MAX_DEPTH = 3;

/**
 * A menu item, with whatever hangs under it. Recursive: a child is the same shape as its parent.
 *
 * ⚠ `href` IS OPTIONAL HERE AND REQUIRED ON `ResolvedMenuItem`, which is why this is a second type
 * rather than a field bolted onto the first. Only an item WITH CHILDREN may be label-only — "Programmes"
 * that opens a submenu and points nowhere itself. A leaf with nowhere to go is dropped, so every leaf a
 * renderer sees still has a destination and no renderer has to defend against a link with no href.
 */
export interface ResolvedMenuNode {
  id: string;
  label: string;
  href?: string;
  external: boolean;
  /** 0 for a top-level item, 1 for a child, 2 for a grandchild. Renderers indent by it. */
  depth: number;
  children: ResolvedMenuNode[];
}

/** `drop` = it had a destination and the destination is not honourable. `label` = it had none at all. */
type Resolution = { href: string; external: boolean } | 'label' | 'drop';

/**
 * One item's destination, checked.
 *
 * ⚠ EVERY CALLER GOES THROUGH HERE, and that is deliberate: this is where `safeExternalUrl` is applied
 * and where a missing page is caught. A second copy of either check is a second place for them to drift,
 * and the one that drifts is the one that renders `javascript:` into every page of the site.
 */
function resolveOne(item: SiteMenuItem, available: ReadonlySet<string>): Resolution {
  if (!item.target) return 'label';

  if (item.target.kind === 'external') {
    const href = safeExternalUrl(item.target.url);
    return href ? { href, external: true } : 'drop';
  }

  if (!available.has(item.target.slug)) return 'drop';

  const path = item.target.slug === 'home' ? '/' : `/${item.target.slug}`;
  return {
    href: item.target.kind === 'anchor' ? `${path}#${item.target.hash}` : path,
    external: false
  };
}

/**
 * Turns stored items into renderable ones, dropping anything that cannot be honoured.
 *
 * ⚠ A MENU ITEM CAN OUTLIVE ITS TARGET, AND THE CHECK HAS TO BE HERE RATHER THAN AT SAVE. A page can be
 * deleted, or exist but not be published in the locale being read — and a save cannot know what a later
 * publish will contain. So the menu is resolved against the pages that ARE available, at render.
 *
 * ⚠ DROPPED, NOT DISABLED. A greyed-out nav item tells a visitor nothing they can act on; an item that
 * is not there is simply a shorter menu, which is the honest outcome for a page that no longer exists.
 *
 * ⚠ FLAT, AND IT IGNORES `parentId`. This is what the FOOTER draws, where nesting has no meaning — the
 * footer's structure is `group`. The header uses `menuTree`. A label-only item is dropped here for the
 * same reason a broken one is: in a flat list it is a word that does nothing.
 */
export function resolveMenu(
  items: readonly SiteMenuItem[],
  availableSlugs: readonly string[]
): ResolvedMenuItem[] {
  const available = new Set(availableSlugs);
  const out: ResolvedMenuItem[] = [];

  for (const item of items) {
    if (!item.visible) continue;

    const resolved = resolveOne(item, available);
    if (resolved === 'drop' || resolved === 'label') continue;

    out.push({
      id: item.id,
      label: item.label,
      href: resolved.href,
      external: resolved.external,
      group: item.group
    });
  }

  return out;
}

/**
 * The chain of ancestors above an item, nearest LAST, or null when the chain is circular.
 *
 * ⚠ THE CYCLE GUARD IS NOT THEORETICAL, AND IT RUNS WHILE RENDERING A HEADER. `parentId` is a stored
 * string, so two items can name each other after an edit, a menu copied between locales, or a restore of
 * an older release — and an unguarded walk would be an infinite loop on the most-visited element of the
 * site, not an error somebody reads in a console later. Mutation-checked: removing the bound makes the
 * test suite run until it is killed.
 *
 * ⚠ AN ORPHAN `parentId` ENDS THE CHAIN INSTEAD OF INVALIDATING IT. A pointer to an item that was
 * deleted is a lost grouping; losing the link as well would be losing content over a lost grouping.
 */
function ancestorChain(
  item: SiteMenuItem,
  byId: ReadonlyMap<string, SiteMenuItem>,
  limit: number
): SiteMenuItem[] | null {
  const chain: SiteMenuItem[] = [];
  let current = item;

  while (chain.length <= limit) {
    const parentId = current.parentId;
    if (!parentId || parentId === current.id) break;

    const parent = byId.get(parentId);
    if (!parent) break;

    chain.unshift(parent);
    current = parent;
  }

  // Longer than the item count means the walk went round rather than up.
  return chain.length > limit ? null : chain;
}

/**
 * The parent each item will actually hang under, with depth clamped to `MENU_MAX_DEPTH`.
 *
 * ⚠ CLAMPED, NOT REJECTED. An item nested deeper than the site draws attaches to the deepest ancestor
 * that still fits, rather than being dropped or rendered at a level with no styling. Silently deleting a
 * link because somebody nested it one step too far is the failure this whole feature exists to avoid —
 * the link survives, one level shallower than intended, which is visible and fixable in the console.
 *
 * ⚠ AN ITEM IN A CYCLE IS EXCLUDED, and that is the one case where content IS dropped. Everywhere else
 * there is a defensible place to put the item; inside a cycle its position is genuinely undefined, and
 * inventing one would put a link somewhere nobody chose.
 */
function effectiveParents(items: readonly SiteMenuItem[]): Map<string, string | undefined> {
  const byId = new Map(items.map((item) => [item.id, item]));
  const parents = new Map<string, string | undefined>();

  for (const item of items) {
    const chain = ancestorChain(item, byId, items.length);
    if (!chain) continue; // circular — see above

    // chain.length IS the natural depth: 0 = top level, 1 = child, 2 = grandchild.
    const depth = Math.min(chain.length, MENU_MAX_DEPTH - 1);
    parents.set(item.id, depth === 0 ? undefined : chain[depth - 1]!.id);
  }

  return parents;
}

/**
 * The header's items as a tree, at most `MENU_MAX_DEPTH` levels deep.
 *
 * ⚠ HIDING AN ITEM HIDES ITS BRANCH. Its children have no disclosure left to appear under, so keeping
 * them would either promote them a level — inventing navigation the admin never wrote — or render them
 * nowhere at all. Hiding the branch is what the switch appears to promise.
 *
 * ⚠ BUT AN ITEM WHOSE OWN TARGET DIED KEEPS ITS CHILDREN, as a label. Otherwise deleting one page would
 * take a whole section of the navigation with it, and the pages underneath — which are fine, and
 * published — would go unreachable from the header.
 */
export function menuTree(
  items: readonly SiteMenuItem[],
  availableSlugs: readonly string[]
): ResolvedMenuNode[] {
  const available = new Set(availableSlugs);
  const parents = effectiveParents(items);

  // Grouped once, in array order, so the recursion below is a lookup rather than a scan per level.
  const byParent = new Map<string | undefined, SiteMenuItem[]>();
  for (const item of items) {
    if (!parents.has(item.id)) continue;
    const key = parents.get(item.id);
    const bucket = byParent.get(key);
    if (bucket) bucket.push(item);
    else byParent.set(key, [item]);
  }

  // ⚠ TERMINATES BECAUSE `effectiveParents` HAS ALREADY REMOVED EVERY CYCLE. The pointers this walks are
  // a forest by construction, which is the only reason a plain recursion is safe here.
  const build = (parentId: string | undefined, depth: number): ResolvedMenuNode[] => {
    const out: ResolvedMenuNode[] = [];

    for (const item of byParent.get(parentId) ?? []) {
      if (!item.visible) continue;

      const children = build(item.id, depth + 1);
      const resolved = resolveOne(item, available);

      if (resolved === 'drop' || resolved === 'label') {
        // No destination of its own, so it only earns a place if something hangs under it.
        if (children.length > 0) {
          out.push({ id: item.id, label: item.label, external: false, depth, children });
        }
        continue;
      }

      out.push({
        id: item.id,
        label: item.label,
        href: resolved.href,
        external: resolved.external,
        depth,
        children
      });
    }

    return out;
  };

  return build(undefined, 0);
}

/**
 * Each item's depth after clamping — 0 top level, 1 child, 2 grandchild.
 *
 * ⚠ THE CONSOLE MUST INDENT BY THIS, NOT BY "HAS A PARENT". With one level those were the same number,
 * so the editor indented on `parentId` being set; with grandchildren they diverge, and a row indented one
 * step under a row that is itself indented one step reads as a sibling. An item in a cycle is absent
 * from the map for the same reason it is absent from the tree.
 */
export function menuDepths(items: readonly SiteMenuItem[]): Map<string, number> {
  const parents = effectiveParents(items);
  const depths = new Map<string, number>();

  for (const [id] of parents) {
    let depth = 0;
    let cursor = parents.get(id);

    // Bounded by the item count: `effectiveParents` has already removed every cycle.
    while (cursor !== undefined && depth <= items.length) {
      depth += 1;
      cursor = parents.get(cursor);
    }

    depths.set(id, depth);
  }

  return depths;
}

/**
 * Whether `parentId` may be set to `candidateId` — the rule the editor's picker enforces.
 *
 * ⚠ IT IS A SUBTREE QUESTION, NOT AN ITEM QUESTION, and that is why it lives here beside the clamp
 * rather than in the console. Moving an item under a candidate pushes its WHOLE subtree down, so a
 * childless item may nest one level deeper than an item that already carries two levels below it. Asking
 * only about the item's own depth would let the console build a menu the site then silently clamps.
 *
 * Refuses: itself, any of its own descendants (which would make a cycle), and any placement whose
 * deepest descendant would fall past `MENU_MAX_DEPTH`.
 */
export function canNestUnder(
  items: readonly SiteMenuItem[],
  itemId: string,
  candidateId: string
): boolean {
  if (itemId === candidateId) return false;

  const parents = effectiveParents(items);
  // An item in a cycle has no defined position, so it has no defined legal move either.
  if (!parents.has(itemId) || !parents.has(candidateId)) return false;

  const depthOf = (id: string): number => {
    let depth = 0;
    let cursor = parents.get(id);
    while (cursor !== undefined && depth <= items.length) {
      depth += 1;
      cursor = parents.get(cursor);
    }
    return depth;
  };

  // Walking UP from the candidate is what catches a descendant, and it is bounded by the forest.
  for (let cursor = parents.get(candidateId); cursor !== undefined; cursor = parents.get(cursor)) {
    if (cursor === itemId) return false;
  }

  const heightOf = (id: string): number => {
    const children = items.filter((item) => parents.get(item.id) === id);
    return children.length === 0 ? 0 : 1 + Math.max(...children.map((child) => heightOf(child.id)));
  };

  return depthOf(candidateId) + 1 + heightOf(itemId) <= MENU_MAX_DEPTH - 1;
}

/** The footer's items grouped into their columns, in first-appearance order. */
export function groupMenu(items: readonly ResolvedMenuItem[]): { group: string; items: ResolvedMenuItem[] }[] {
  const groups: { group: string; items: ResolvedMenuItem[] }[] = [];

  for (const item of items) {
    const key = item.group ?? '';
    const existing = groups.find((g) => g.group === key);
    if (existing) existing.items.push(item);
    else groups.push({ group: key, items: [item] });
  }

  return groups;
}
